import { Component, OnDestroy, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';
import { Subject, finalize, takeUntil } from 'rxjs';

import { IndicacaoService } from '../../../../../services/indicacoes/indicacao.service';
import {
  ComissaoAdminConsultaDTO,
  PagamentoComissaoConsultaDTO,
  PagamentoComissaoFiltroDTO,
  PagamentoComissaoRelatorioFiltroDTO,
  StatusRepasse,
} from '../../../../../models/indicacoes/indicacao.model';
import { PeriodoFiltro } from '../../../../../models/dashboard/periodo-filtro.model';
import { FileUtils } from '../../../../../shared/utils/file-utils';

/** Campos de ordenacao aceitos pelo backend (whitelist do contrato). */
const CAMPOS_ORDENACAO: string[] = ['dataPagamento', 'valorTotal', 'id'];

/** Severities aceitas pelo p-tag do PrimeNG (mesmo tipo da aba irma). */
type TagSeverity = 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast';

/**
 * Subcomponente da aba "Historico de Pagamentos" (master-only, READ-ONLY).
 *
 * Lista os EVENTOS de pagamento de comissao ja realizados (GET /comissoes/pagamentos),
 * paginados server-side, com filtro de periodo, de pessoa indicadora e de comissao de
 * origem (em CASCATA), detalhe expansivel das comissoes abatidas e download do
 * comprovante PDF por linha. Acesso restrito via `masterAccessGuard` na rota pai.
 * Fonte: `vendas/docs/api/API_CONTRACT_historico-pagamento-comissoes.md`.
 */
@Component({
  selector: 'app-historico-pagamentos',
  templateUrl: './historico-pagamentos.component.html',
  styleUrls: ['./historico-pagamentos.component.scss'],
  providers: [MessageService],
})
export class HistoricoPagamentosComponent implements OnInit, OnDestroy {
  /** Encerra as assinaturas no ngOnDestroy. */
  private destroy$ = new Subject<void>();

  // ===========================================================================
  // Listagem (tabela lazy)
  // ===========================================================================

  pagamentos: PagamentoComissaoConsultaDTO[] = [];
  first = 0;
  rows = 10;
  totalRecords = 0;
  loading = true;
  erroCarregar = false;

  /** Opcoes do dropdown "itens por pagina" do footer. */
  pageSizeOptions = [
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
  ];

  /** Ultimo valor de "itens por pagina" ja carregado; descarta o (ngModelChange)
   *  que o dropdown dispara sem o usuario mudar a selecao. */
  private _lastRows = 10;

  /** Ordenacao atual "campo,direcao" (default do contrato: dataPagamento,desc). */
  sortAtual = 'dataPagamento,desc';

  /**
   * Linhas expandidas (detalhe das comissoes de origem). Mapa manual — mesmo idioma
   * de `contas-a-receber` / `lista-titulos`, sem a API `rowExpansion` do PrimeNG.
   * Zerado a cada carga: a tabela e lazy, e o id da pagina 1 nao e o da pagina 2.
   */
  expandedRows: { [key: number]: boolean } = {};

  // ===========================================================================
  // Filtros
  // ===========================================================================

  /** Periodo de `dataPagamento` — strings CRUAS do app-periodo-filtro
   *  ("dd/MM/yyyy'T'HH:mm"). Proibida qualquer conversao para ISO (=> 422). */
  dataPagamentoInicio: string | undefined = undefined;
  dataPagamentoFim: string | undefined = undefined;

  /** Empresa indicadora que recebeu o pagamento (`indicadorId`). `null` = todas. */
  indicadorSelecionadoId: number | null = null;

  /** Comissao de origem (`comissaoId`) — so pode ir com indicador (cascata). */
  comissaoSelecionadaId: number | null = null;

  /** Opcoes do dropdown de indicador (derivadas de /comissoes — sem endpoint dedicado). */
  indicadorOptions: { label: string; value: number }[] = [];

  /** Opcoes do dropdown de comissao de origem (vazio enquanto nao ha indicador). */
  comissaoOptions: { label: string; value: number }[] = [];

  /** Carga do combo de comissoes em andamento. */
  carregandoComissoes = false;

  // ===========================================================================
  // Acao — comprovante
  // ===========================================================================

  /** Id do pagamento cujo comprovante esta sendo baixado (spinner na linha +
   *  guarda anti-clique-duplo). `null` = nenhuma impressao em andamento. */
  imprimindoPagamentoId: number | null = null;

  // ===========================================================================
  // Acao — exportar (menu PDF / Excel / CSV do historico com os filtros atuais)
  // ===========================================================================

  /** Itens do menu popup "Exportar" (PDF / Excel .xlsx / CSV). Reconstruido no onLangChange. */
  exportarItens: MenuItem[] = [];

  /** Guarda anti-clique-duplo compartilhada pelos 3 formatos (spinner + [disabled]/[loading]). */
  exportando = false;

  constructor(
    private indicacaoService: IndicacaoService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.montarExportarItens();
    // Subscricao NOVA: reconstroi os rotulos do menu "Exportar" ao trocar o idioma
    // (pt-BR <-> es), sem reabrir a tela. Usa o destroy$ existente (sem vazamento).
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.montarExportarItens();
    });
    // A carga da TABELA e disparada pelo (onLazyLoad) da p-table (padrao da aba irma).
    this.carregarIndicadores();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===========================================================================
  // Listagem
  // ===========================================================================

  private carregar(page: number, size: number): void {
    this.loading = true;
    this.erroCarregar = false;
    // R12: a expansao vale para a visao atual (tabela lazy server-side). Sem este
    // reset, a linha expandida da pagina 1 reabriria na pagina 2 (mesmo id no mapa).
    this.expandedRows = {};

    this.indicacaoService
      .listarHistoricoPagamentos(this.construirFiltros(page, size))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // Envelope `Page` DIRETO: `res.content` — NUNCA `res.comissoes.content`.
          this.pagamentos = res?.content || [];
          this.totalRecords = res?.totalElements ?? 0;
          this.first = page * size;
          this.loading = false;
        },
        error: (err) => {
          this.pagamentos = [];
          this.totalRecords = 0;
          this.erroCarregar = true;
          this.loading = false;
          this.messageService.add({ severity: 'error', summary: this.mensagemErroCarregar(err) });
        },
      });
  }

  /**
   * Mensagem do erro de carga. A listagem usa `read` (JSON), entao `err.error` chega
   * como OBJETO e `err.error.message` funciona — mas o repasse dessa mensagem e
   * restrito ao 422 (DESIGN 8.1), o unico status em que o backend garante texto
   * amigavel ja traduzido (cascata / formato de data / intervalo invertido). Em 5xx
   * a `message` costuma ser tecnica (excecao JDBC etc.) e NAO pode chegar ao usuario
   * final: cai no fallback i18n. 401 nao e tratado aqui (o AuthInterceptor limpa a
   * sessao e redireciona).
   */
  private mensagemErroCarregar(err: any): string {
    if (err?.status === 403) {
      return this.translate.instant('contador.historico_pagamentos.erros.sem_permissao');
    }
    if (err?.status === 422 && err?.error?.message) {
      return err.error.message;
    }
    return this.translate.instant('contador.historico_pagamentos.erro_carregar');
  }

  /**
   * Monta os query params a partir do estado da tela. `null`/'' viram `undefined`
   * porque o `buildQueryParams` do ApiService descarta APENAS `undefined`.
   * Camada 3 da cascata: `comissaoId` NUNCA sai sem `indicadorId` (evita o 422
   * `comissao_pagamento_filtro_comissao_sem_indicador`).
   */
  private construirFiltros(page: number, size: number): PagamentoComissaoFiltroDTO {
    const indicadorId = this.indicadorSelecionadoId ?? undefined;
    return {
      dataPagamentoInicio: this.dataPagamentoInicio,
      dataPagamentoFim: this.dataPagamentoFim,
      indicadorId,
      comissaoId: indicadorId != null ? (this.comissaoSelecionadaId ?? undefined) : undefined,
      page,
      size,
      sort: this.sortAtual,
    };
  }

  /** Carregamento lazy da tabela (paginacao + ordenacao server-side). */
  onLazyLoad(event: TableLazyLoadEvent): void {
    const size = event.rows ?? this.rows;
    const page = Math.floor((event.first ?? 0) / size);
    this.rows = size;
    this._lastRows = size;

    const campo = (event.sortField as string) || 'dataPagamento';
    const campoValido = CAMPOS_ORDENACAO.includes(campo) ? campo : 'dataPagamento';
    const direcao = event.sortOrder === 1 ? 'asc' : 'desc';
    this.sortAtual = `${campoValido},${direcao}`;

    this.carregar(page, size);
  }

  /** Troca de pagina pelo paginador externo (tabela lazy: recarrega do backend). */
  onPageChange(event: { page?: number; rows?: number }): void {
    const size = event.rows ?? this.rows;
    const page = event.page ?? 0;
    this.rows = size;
    this._lastRows = size;
    this.carregar(page, size);
  }

  /** Troca de "itens por pagina": volta para a primeira pagina e recarrega. */
  onRowsPerPageChange(): void {
    if (this.rows === this._lastRows) {
      return;
    }
    this._lastRows = this.rows;
    this.first = 0;
    this.carregar(0, this.rows);
  }

  /** Aplica os filtros atuais e recarrega a partir da primeira pagina. */
  aplicarFiltros(): void {
    this.first = 0;
    this.carregar(0, this.rows);
  }

  // ===========================================================================
  // Filtros
  // ===========================================================================

  /**
   * Guarda o par de datas do app-periodo-filtro (strings CRUAS "dd/MM/yyyy'T'HH:mm",
   * sem nenhuma conversao — ISO seria 422) e recarrega. O fim ja e inclusivo no
   * backend: nao somar 1 dia aqui.
   */
  onPeriodoChanged(p: PeriodoFiltro): void {
    this.dataPagamentoInicio = p.dataInicio || undefined;
    this.dataPagamentoFim = p.dataFim || undefined;
    this.aplicarFiltros();
  }

  /**
   * Popula o dropdown de indicadores. Como NAO ha endpoint dedicado, derivamos os
   * DISTINTOS de uma carga ampla de /comissoes (mesma abordagem da aba irma).
   * Falha silenciosa: zera as opcoes, sem toast (filtro auxiliar).
   */
  private carregarIndicadores(): void {
    this.indicacaoService
      .listarComissoesAdmin({ page: 0, size: 500, sort: 'geradoEm,desc' })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const distintos = new Map<number, string>();
          for (const c of res.comissoes?.content || []) {
            if (c.indicadorEmpresaId != null && !distintos.has(c.indicadorEmpresaId)) {
              distintos.set(c.indicadorEmpresaId, c.indicadorNome || `#${c.indicadorEmpresaId}`);
            }
          }
          this.indicadorOptions = Array.from(distintos, ([value, label]) => ({ value, label })).sort(
            (a, b) => a.label.localeCompare(b.label),
          );
        },
        error: () => {
          this.indicadorOptions = [];
        },
      });
  }

  /**
   * Camada 2 da cascata: trocar (ou limpar) o indicador zera a comissao selecionada
   * e as opcoes ANTES de qualquer consulta; so entao repopula o combo e recarrega.
   */
  onIndicadorChange(): void {
    this.comissaoSelecionadaId = null;
    this.comissaoOptions = [];
    if (this.indicadorSelecionadoId != null) {
      this.carregarComissoesDoIndicador(this.indicadorSelecionadoId);
    }
    this.aplicarFiltros();
  }

  /**
   * Popula o combo "comissao de origem" com as comissoes do indicador escolhido
   * (GET /comissoes?indicadorId=). Envelope `{ resumo, comissoes: Page }` — aqui SIM
   * o acesso e `res.comissoes.content` (endpoint diferente do historico).
   *
   * R11: descarta a resposta cujo `indicadorId` nao seja mais o selecionado. Sem a
   * guarda, trocar de indicador rapidamente popularia o combo com as comissoes do
   * indicador ERRADO e a consulta voltaria pagina vazia sem 422 (falha silenciosa).
   */
  private carregarComissoesDoIndicador(indicadorId: number): void {
    this.carregandoComissoes = true;
    this.indicacaoService
      .listarComissoesAdmin({ indicadorId, page: 0, size: 100, sort: 'competencia,desc' })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.carregandoComissoes = false)),
      )
      .subscribe({
        next: (res) => {
          if (indicadorId !== this.indicadorSelecionadoId) {
            return; // resposta obsoleta (o usuario ja trocou de indicador)
          }
          this.comissaoOptions = (res.comissoes?.content || []).map((c) => ({
            value: c.id,
            label: this.labelComissao(c),
          }));
        },
        error: () => {
          if (indicadorId === this.indicadorSelecionadoId) {
            this.comissaoOptions = [];
          }
        },
      });
  }

  /** Troca da comissao de origem: recarrega da primeira pagina. */
  onComissaoChange(): void {
    this.aplicarFiltros();
  }

  /** Camada 1 da cascata: sem indicador, o combo de comissao fica desabilitado. */
  get comissaoDesabilitada(): boolean {
    return this.indicadorSelecionadoId == null;
  }

  /** Rotulo da opcao do combo: "25/05/2026 — Gs. 25.000". */
  private labelComissao(c: ComissaoAdminConsultaDTO): string {
    return `${this.dataLabel(c.competencia)} — ${this.formatarGuarani(c.valorComissao)}`;
  }

  // ===========================================================================
  // Detalhe expansivel (comissoes de origem)
  // ===========================================================================

  /** Alterna a linha de detalhe (idioma de contas-a-receber / lista-titulos). */
  toggleExpand(pagamento: PagamentoComissaoConsultaDTO): void {
    if (this.expandedRows[pagamento.id]) {
      delete this.expandedRows[pagamento.id];
    } else {
      this.expandedRows[pagamento.id] = true;
    }
  }

  isExpandido(pagamento: PagamentoComissaoConsultaDTO): boolean {
    return !!this.expandedRows[pagamento.id];
  }

  // ===========================================================================
  // Acao — comprovante PDF
  // ===========================================================================

  /**
   * Baixa o comprovante (Base64) do EVENTO de pagamento da linha. O id enviado e o
   * `pagamento.id` (PagamentoComissao), nunca o id da comissao. Guarda contra clique
   * duplo pelo `imprimindoPagamentoId`.
   */
  imprimirComprovante(pagamento: PagamentoComissaoConsultaDTO): void {
    if (this.imprimindoPagamentoId != null) {
      return;
    }
    this.imprimindoPagamentoId = pagamento.id;
    this.indicacaoService
      .gerarComprovantePagamentoPdf(pagamento.id)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.imprimindoPagamentoId = null)),
      )
      .subscribe({
        next: (base64) => {
          if (base64 && base64.length > 0) {
            FileUtils.abrirPdf(base64, `Comprovante_Pago_${pagamento.id}.pdf`);
          } else {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant(
                'contador.historico_pagamentos.comprovante.indisponivel',
              ),
            });
          }
        },
        error: (err) => this.tratarErroComprovante(err),
      });
  }

  /**
   * Erro do comprovante: SEMPRE `warn` (acao secundaria de consulta). O endpoint usa
   * `readText` (responseType 'text'), entao `err.error` chega como STRING CRUA — por
   * isso usamos chaves i18n FIXAS por status, sem parsear o corpo.
   */
  private tratarErroComprovante(err: any): void {
    const status = err?.status;
    let chave = 'contador.historico_pagamentos.comprovante.erros.generico';
    if (status === 403) {
      chave = 'contador.historico_pagamentos.comprovante.erros.sem_permissao';
    } else if (status === 404) {
      chave = 'contador.historico_pagamentos.comprovante.erros.nao_encontrado';
    }
    this.messageService.add({ severity: 'warn', summary: this.translate.instant(chave) });
  }

  // ===========================================================================
  // Acao — exportar (menu PDF / Excel / CSV do historico com os filtros atuais)
  // ===========================================================================

  /**
   * Monta os 3 itens do menu popup "Exportar" (PDF / Excel .xlsx / CSV) com os
   * rotulos traduzidos. Chamado no ngOnInit e reconstruido a cada troca de idioma
   * (onLangChange). Ordem e icones espelham a aba "Repasse de Comissoes": PDF usa o
   * fluxo Base64 (`exportarPdf`); Excel/CSV usam o export binario (`exportarArquivo`).
   */
  private montarExportarItens(): void {
    this.exportarItens = [
      {
        label: this.translate.instant('contador.historico_pagamentos.exportar.pdf'),
        icon: 'pi pi-file-pdf',
        command: () => this.exportarPdf(),
      },
      {
        label: this.translate.instant('contador.historico_pagamentos.exportar.excel'),
        icon: 'pi pi-file-excel',
        command: () => this.exportarArquivo('xlsx'),
      },
      {
        label: this.translate.instant('contador.historico_pagamentos.exportar.csv'),
        icon: 'pi pi-file',
        command: () => this.exportarArquivo('csv'),
      },
    ];
  }

  /**
   * Exporta o PDF do historico de pagamentos respeitando os filtros ATUAIS da tela
   * (periodo, indicador, comissao em cascata, ordenacao) — SEM page/size (imprime TODAS
   * as linhas do filtro ate o teto do backend). Sucesso com Base64 nao-vazio => baixa o
   * PDF; Base64 vazio => "sem dados" (defensivo — o backend gera PDF valido mesmo com 0
   * linhas); erro => toast por status. `exportando` desabilita o botao e evita clique duplo.
   */
  exportarPdf(): void {
    if (this.exportando) {
      return; // guarda contra clique duplo
    }
    this.exportando = true;
    this.indicacaoService
      .gerarRelatorioPagamentosPdf(this.construirFiltrosRelatorio())
      .subscribe({
        next: (base64) => {
          this.exportando = false;
          if (base64 && base64.length > 0) {
            FileUtils.abrirPdf(base64, this.nomeArquivoRelatorio('pdf'));
          } else {
            // Defensivo: o contrato diz que 0 pagamentos ainda gera PDF valido (grade vazia).
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('contador.historico_pagamentos.exportar_pdf.sem_dados'),
            });
          }
        },
        error: (err) => {
          this.exportando = false;
          this.tratarErroExportarPdf(err);
        },
      });
  }

  /**
   * Exporta o historico como arquivo binario (.csv/.xlsx) com os filtros ATUAIS da tela
   * (mesmos do PDF, via `construirFiltrosRelatorio`). Corpo => baixa via `FileUtils.baixarBlob`;
   * 0 bytes => toast "sem dados" sem baixar; erro => `tratarErroExportarArquivo`. A flag
   * `exportando` (compartilhada com o PDF) evita clique duplo.
   */
  exportarArquivo(formato: 'csv' | 'xlsx'): void {
    if (this.exportando) {
      return; // guarda contra clique duplo
    }
    this.exportando = true;
    this.indicacaoService
      .gerarRelatorioPagamentosArquivo(formato, this.construirFiltrosRelatorio())
      .subscribe({
        next: (blob) => {
          this.exportando = false;
          if (blob && blob.size > 0) {
            FileUtils.baixarBlob(blob, this.nomeArquivoRelatorio(formato));
          } else {
            // CA05: backend responde 200 com 0 bytes quando nao ha pagamentos no filtro.
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('contador.historico_pagamentos.exportar_pdf.sem_dados'),
            });
          }
        },
        error: (err) => {
          this.exportando = false;
          this.tratarErroExportarArquivo(err);
        },
      });
  }

  /**
   * Monta os filtros do relatorio a partir do estado atual da tela. NAO reutiliza
   * `construirFiltros(page, size)` (que sempre injeta paginacao): o relatorio imprime
   * TUDO. Replica a cascata: `comissaoId` so vai quando ha `indicadorId`. `null`/'' viram
   * `undefined` (o buildQueryParams so descarta `undefined`). `sortAtual` ja e clampado a
   * whitelist em `onLazyLoad`. Datas repassadas CRUAS "dd/MM/yyyy'T'HH:mm" (ISO => 422).
   */
  private construirFiltrosRelatorio(): PagamentoComissaoRelatorioFiltroDTO {
    const indicadorId = this.indicadorSelecionadoId ?? undefined;
    return {
      dataPagamentoInicio: this.dataPagamentoInicio, // string crua ou undefined
      dataPagamentoFim: this.dataPagamentoFim, // string crua ou undefined
      indicadorId,
      comissaoId: indicadorId != null ? (this.comissaoSelecionadaId ?? undefined) : undefined,
      sort: this.sortAtual,
      // page / size: NAO incluidos (nao existem no tipo) => relatorio imprime tudo.
    };
  }

  /** Nome do arquivo baixado por extensao: `Historial_Pagos_AAAAMMDD.{ext}`
   *  (.pdf/.xlsx/.csv; a tela e MASTER/contexto ES — paridade com a aba irma). */
  private nomeArquivoRelatorio(ext: 'pdf' | 'xlsx' | 'csv'): string {
    const hoje = new Date();
    const ano = hoje.getFullYear();
    const mes = String(hoje.getMonth() + 1).padStart(2, '0');
    const dia = String(hoje.getDate()).padStart(2, '0');
    return `Historial_Pagos_${ano}${mes}${dia}.${ext}`;
  }

  /**
   * Trata os erros do relatorio PDF. 403 sem permissao (reuso da chave existente
   * `historico_pagamentos.erros.sem_permissao`); 422 (teto de 5000 OU cascata/periodo)
   * exibindo a mensagem amigavel do backend (com fallback `exportar_pdf.limite_excedido`);
   * generico/500 com fallback `exportar_pdf.erro`. Como o relatorio usa `readText`
   * (responseType 'text'), o corpo de `err.error` chega como STRING CRUA — extraimos a
   * mensagem de forma defensiva (JSON.parse) via `extrairMensagemBackend`.
   */
  private tratarErroExportarPdf(err: any): void {
    const status = err?.status;

    if (status === 403) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('contador.historico_pagamentos.erros.sem_permissao'),
      });
      return;
    }

    const mensagemBackend = this.extrairMensagemBackend(err);

    if (status === 422) {
      this.messageService.add({
        severity: 'error',
        summary:
          mensagemBackend ||
          this.translate.instant('contador.historico_pagamentos.exportar_pdf.limite_excedido'),
      });
      return;
    }

    this.messageService.add({
      severity: 'error',
      summary:
        mensagemBackend ||
        this.translate.instant('contador.historico_pagamentos.exportar_pdf.erro'),
    });
  }

  /**
   * Trata o erro do export binario. Como `readBlob` entrega `err.error` como Blob (nao
   * parseavel de forma sincrona), roteamos a mensagem i18n so por `err.status`: 403 sem
   * permissao (chave reutilizada), 422 limite excedido, generico caso contrario.
   */
  private tratarErroExportarArquivo(err: any): void {
    const status = err?.status;
    let chave = 'contador.historico_pagamentos.exportar_pdf.erro'; // generico/500
    if (status === 403) {
      chave = 'contador.historico_pagamentos.erros.sem_permissao'; // reuso (RN06)
    } else if (status === 422) {
      chave = 'contador.historico_pagamentos.exportar.limite_excedido';
    }
    this.messageService.add({ severity: 'error', summary: this.translate.instant(chave) });
  }

  /**
   * Extrai a mensagem amigavel do backend de forma robusta ao `responseType`. Chamadas
   * `read` (JSON) entregam `err.error` como OBJETO (`{ message }`); chamadas `readText`
   * (usada pelo relatorio PDF) entregam `err.error` como STRING CRUA (JSON nao parseado).
   * Cobrimos os dois casos: objeto -> `.message`; string -> `JSON.parse` e `.message`.
   * Corpo ausente ou texto nao-JSON retorna `undefined` (o chamador aplica o fallback i18n).
   */
  private extrairMensagemBackend(err: any): string | undefined {
    const corpo = err?.error;
    if (!corpo) {
      return undefined;
    }
    if (typeof corpo === 'object') {
      return typeof corpo.message === 'string' ? corpo.message : undefined;
    }
    if (typeof corpo === 'string') {
      try {
        const parsed = JSON.parse(corpo);
        return typeof parsed?.message === 'string' ? parsed.message : undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  // ===========================================================================
  // Helpers de exibicao
  // ===========================================================================

  /**
   * "2026-07-15T14:22:10" -> "15/07/2026 14:22". Parsing por STRING (sem `new Date`)
   * para nao deslocar o valor por fuso horario. Sem match => "—".
   */
  dataHoraLabel(iso: string | null | undefined): string {
    if (!iso) {
      return '—';
    }
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (m) {
      return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
    }
    return this.dataLabel(iso);
  }

  /** "2026-05-25T00:00:00" -> "25/05/2026". Sem match => "—". */
  dataLabel(iso: string | null | undefined): string {
    if (!iso) {
      return '—';
    }
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '—';
  }

  labelStatus(status: StatusRepasse | string | undefined): string {
    if (!status) {
      return '';
    }
    return this.translate.instant(`contador.historico_pagamentos.status.${status}`);
  }

  severityStatus(status: StatusRepasse | string | undefined): TagSeverity {
    switch (status) {
      case 'PAGA':
        return 'success';
      case 'PENDENTE':
        return 'warning';
      case 'CANCELADA':
        return 'danger';
      default:
        return 'info';
    }
  }

  /** Formata o valor apenas para compor o label do combo (na tabela usa-se o pipe). */
  private formatarGuarani(valor: number): string {
    return `Gs. ${(valor ?? 0).toLocaleString('es-PY', { maximumFractionDigits: 0 })}`;
  }
}
